import { useEffect, useState } from "react";
import { Minus, Plus, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatNaira } from "@/data/menu";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function ItemDetailsModal() {
  const { detailItem, closeDetails, addItem, openCart } = useCart();
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (detailItem) setQty(1);
  }, [detailItem]);

  if (!detailItem) return null;

  const handleAdd = () => {
    addItem(detailItem, qty);
    toast.success(`${qty} × ${detailItem.name} added to cart`);
    closeDetails();
  };

  const handleAddAndOpen = () => {
    addItem(detailItem, qty);
    toast.success(`${qty} × ${detailItem.name} added`);
    closeDetails();
    setTimeout(() => openCart(), 200);
  };

  const lineTotal = detailItem.price * qty;

  return (
    <Dialog open={!!detailItem} onOpenChange={(o) => !o && closeDetails()}>
      <DialogContent
        data-testid="item-details-modal"
        className="sm:max-w-md bg-[#FFFCF5] border-[#E8E2D6] p-0 overflow-hidden"
      >
        <button
          type="button"
          onClick={closeDetails}
          data-testid="item-details-close"
          className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-[#E8E2D6] grid place-items-center hover:bg-white"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="bg-gradient-to-br from-[#344E41] to-[#1f3128] text-white px-6 py-7">
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/70 font-semibold">
            Menu Item
          </p>
          <DialogTitle
            data-testid="item-details-name"
            className="mt-2 font-display text-3xl font-extrabold tracking-tight leading-tight"
          >
            {detailItem.name}
          </DialogTitle>
          <p
            data-testid="item-details-price"
            className="mt-3 text-[#F2B441] font-display text-2xl font-extrabold"
          >
            {formatNaira(detailItem.price)}
          </p>
        </div>

        <div className="px-6 py-5">
          <DialogDescription
            data-testid="item-details-desc"
            className="text-[#2C2825]/80 text-sm leading-relaxed"
          >
            {detailItem.desc || "A Meritz classic — freshly prepared to order."}
          </DialogDescription>

          <div className="mt-5 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6B635A]">
              Quantity
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="item-details-qty-minus"
                className="w-9 h-9 rounded-full border border-[#E8E2D6] bg-white grid place-items-center hover:bg-[#F9F6F0]"
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span
                data-testid="item-details-qty"
                className="font-display font-extrabold text-xl w-8 text-center"
              >
                {qty}
              </span>
              <button
                type="button"
                onClick={() => setQty((q) => q + 1)}
                data-testid="item-details-qty-plus"
                className="w-9 h-9 rounded-full border border-[#E8E2D6] bg-white grid place-items-center hover:bg-[#F9F6F0]"
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-[#E8E2D6] flex items-baseline justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#6B635A]">
              Subtotal
            </span>
            <span
              data-testid="item-details-line-total"
              className="font-display text-2xl font-extrabold text-[#C73E3A]"
            >
              {formatNaira(lineTotal)}
            </span>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleAdd}
              data-testid="item-details-add"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#2C2825] text-[#2C2825] font-semibold py-3 rounded-full hover:bg-[#2C2825] hover:text-white transition-colors"
            >
              <ShoppingBag size={16} /> Add
            </button>
            <button
              type="button"
              onClick={handleAddAndOpen}
              data-testid="item-details-add-checkout"
              className="inline-flex items-center justify-center gap-2 bg-[#C73E3A] text-white font-semibold py-3 rounded-full hover:bg-[#a52f2c] transition-colors"
            >
              Add & Checkout
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
